<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	import SeoContent from '$lib/components/sections/SeoContent.svelte';
	import Button from '$lib/components/primitives/Button.svelte';
	import Card from '$lib/components/primitives/Card.svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	const browser = $derived({
		name: 'Chrome',
		icon: 'chrome',
		users: '2B+',
		storeUrl: 'https://chrome.google.com/webstore/detail/heyweek',
		description: $_('products.extensions.browsers.chrome.description'),
		available: true
	});

	const features = $derived([
		{
			icon: 'play-circle',
			title: $_('products.extensions.features.quickStart.title'),
			description: $_('products.extensions.features.quickStart.description')
		},
		{
			icon: 'link-2',
			title: $_('products.extensions.features.autoDetect.title'),
			description: $_('products.extensions.features.autoDetect.description')
		},
		{
			icon: 'eye-off',
			title: $_('products.extensions.features.privacy.title'),
			description: $_('products.extensions.features.privacy.description')
		},
		{
			icon: 'command',
			title: $_('products.extensions.features.shortcuts.title'),
			description: $_('products.extensions.features.shortcuts.description')
		}
	]);

	const integrations = $derived([
		'GitHub', 'GitLab', 'Jira', 'Asana', 'Trello', 'Linear', 
		'Notion', 'Monday.com', 'ClickUp', 'Basecamp'
	]);
</script>

<svelte:head>
	<title>{$_('products.extensions.meta.title')}</title>
	<meta name="description" content={$_('products.extensions.meta.description')} />
	<meta property="og:title" content="{$_('products.extensions.meta.title')}" />
	<meta property="og:description" content={$_('products.extensions.meta.description')} />
</svelte:head>

<Header />

<div class="product-page">
	<!-- Hero Section -->
	<section class="hero">
		<div class="container">
			<div class="hero__content">
				<div class="hero__badge">
					<Icon name="puzzle" size={16} />
					<span>{$_('products.extensions.badge')}</span>
				</div>
				<h1 class="hero__title">
					{$_('products.extensions.hero.title')}
				</h1>
				<p class="hero__subtitle">
					{$_('products.extensions.hero.subtitle')}
				</p>
			</div>
		</div>
	</section>

	<!-- Browsers Section -->
	<section id="browsers" class="browsers">
		<div class="container">
			<div class="browsers__header">
				<h2 class="browsers__title">{$_('products.extensions.browsers.title')}</h2>
				<p class="browsers__subtitle">{$_('products.extensions.browsers.subtitle')}</p>
			</div>
			
			<div class="browsers__single">
				<Card class="browser-card browser-card--available">
					<div class="browser-card__header">
						<div class="browser-card__icon">
							<Icon name={browser.icon} size={32} />
						</div>
						<div class="browser-card__info">
							<h3 class="browser-card__name">{browser.name}</h3>
							<span class="browser-card__users">{browser.users} {$_('products.extensions.browsers.users')}</span>
						</div>
					</div>
					<p class="browser-card__description">
						{browser.description}
					</p>
					<Button href={browser.storeUrl} variant="primary" size="md">
						<Icon name="download" size={18} />
						{$_('products.extensions.browsers.installButton')}
					</Button>
				</Card>
				
				<div class="more-coming">
					<Icon name="sparkles" size={20} />
					<h3 class="more-coming__title">{$_('products.extensions.browsers.moreComing.title')}</h3>
					<p class="more-coming__text">{$_('products.extensions.browsers.moreComing.description')}</p>
				</div>
			</div>
		</div>
	</section>

	<!-- Features Section -->
	<section id="features" class="features">
		<div class="container">
			<div class="features__grid">
				<div class="features__content">
					<h2 class="features__title">{$_('products.extensions.features.title')}</h2>
					<div class="features__list">
						{#each features as feature}
							<div class="feature-item">
								<div class="feature-item__icon">
									<Icon name={feature.icon} size={20} style="color: rgb(255, 94, 126);" />
								</div>
								<div class="feature-item__content">
									<h3 class="feature-item__title">{feature.title}</h3>
									<p class="feature-item__description">{feature.description}</p>
								</div>
							</div>
						{/each}
					</div>
				</div>
				<div class="features__visual">
					<div class="features__image">
						<Icon name="puzzle" size={80} style="color: rgb(255, 94, 126); opacity: 0.8;" />
					</div>
				</div>
			</div>
		</div>
	</section>

	<!-- Integrations Section -->
	<section class="integrations">
		<div class="container">
			<div class="integrations__header">
				<h2 class="integrations__title">{$_('products.extensions.integrations.title')}</h2>
				<p class="integrations__subtitle">{$_('products.extensions.integrations.subtitle')}</p>
			</div>
			<div class="integrations__grid">
				{#each integrations as integration}
					<div class="integration-tag">
						{integration}
					</div>
				{/each}
			</div>
		</div>
	</section>

	<!-- CTA Section -->
	<section class="cta">
		<div class="container">
			<div class="cta__content">
				<h2 class="cta__title">{$_('products.extensions.cta.title')}</h2>
				<p class="cta__subtitle">{$_('products.extensions.cta.subtitle')}</p>
				<div class="cta__actions">
					<Button href="https://chrome.google.com/webstore/detail/heyweek" variant="secondary" size="lg" class="cta__button">
						<Icon name="chrome" size={20} />
						{$_('products.extensions.cta.chromeButton')}
					</Button>
					<Button href="/products/web" variant="ghost" size="lg" class="cta__secondary">
						{$_('products.extensions.cta.webButton')}
					</Button>
				</div>
			</div>
		</div>
	</section>
</div>

<SeoContent slug="products-extensions" />

<Footer />

<style>
	.product-page {
		min-height: 100vh;
		padding-top: var(--space-8);
	}

	/* Override Tailwind's container with proper constraints */
	:global(.container) {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 1rem;
		width: 100%;
	}

	/* Hero Section */
	.hero {
		padding: var(--space-16) 0;
		background: linear-gradient(180deg, var(--bg-secondary) 0%, transparent 100%);
		text-align: center;
	}

	.hero__content {
		max-width: 800px;
		margin: 0 auto;
	}

	.hero__badge {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-1) var(--space-3);
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-full);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		margin-bottom: var(--space-4);
	}

	:global(.dark) .hero__badge {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.hero__title {
		font-size: var(--font-size-5xl);
		font-weight: var(--font-weight-bold);
		line-height: 1.1;
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		letter-spacing: -0.02em;
	}

	.hero__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
		max-width: 600px;
		margin-left: auto;
		margin-right: auto;
	}


	/* Browsers Section */
	.browsers {
		padding: var(--space-20) 0;
	}

	.browsers__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.browsers__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.browsers__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto;
	}

	.browsers__single {
		max-width: 500px;
		margin: 0 auto;
		display: flex;
		flex-direction: column;
		gap: var(--space-8);
		align-items: center;
	}
	
	.more-coming {
		text-align: center;
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: var(--space-2);
		width: 100%;
	}
	
	.more-coming__title {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin: 0;
	}
	
	.more-coming__text {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		margin: 0;
	}
	
	:global(.dark) .more-coming {
		background: var(--bg-tertiary);
	}

	:global(.browser-card) {
		padding: var(--space-8);
		transition: all 0.2s ease;
		border: 1px solid var(--border-light);
		width: 100%;
	}

	:global(.browser-card:hover) {
		transform: translateY(-4px);
		border-color: var(--border-brand);
		box-shadow: var(--shadow-elevated);
	}

	:global(.dark .browser-card) {
		border-color: var(--border-default);
	}

	:global(.dark .browser-card:hover) {
		border-color: var(--border-brand);
	}

	.browser-card__header {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		margin-bottom: var(--space-4);
	}

	.browser-card__icon {
		flex-shrink: 0;
		width: 48px;
		height: 48px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		border-radius: var(--radius-lg);
		color: rgb(255, 94, 126);
	}

	:global(.dark) .browser-card__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.browser-card__info {
		flex: 1;
	}

	.browser-card__name {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}

	.browser-card__users {
		font-size: var(--font-size-xs);
		color: var(--text-tertiary);
	}

	.browser-card__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
		margin-bottom: var(--space-4);
	}

	:global(.browser-card--coming-soon) {
		opacity: 0.7;
		position: relative;
	}

	:global(.browser-card--coming-soon:hover) {
		transform: none;
		border-color: var(--border-light);
		box-shadow: var(--shadow-base);
	}

	:global(.dark .browser-card--coming-soon:hover) {
		border-color: var(--border-default);
	}



	/* Features Section */
	.features {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
	}

	.features__grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-12);
		align-items: center;
	}

	.features__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
	}

	.features__list {
		display: flex;
		flex-direction: column;
		gap: var(--space-6);
	}

	.feature-item {
		display: flex;
		gap: var(--space-4);
	}

	.feature-item__icon {
		flex-shrink: 0;
		width: 40px;
		height: 40px;
		display: flex;
		align-items: center;
		justify-content: center;
		background: rgba(255, 94, 126, 0.1);
		color: rgb(255, 94, 126);
		border-radius: var(--radius-lg);
	}

	:global(.dark) .feature-item__icon {
		background: rgba(255, 94, 126, 0.15);
		color: rgb(255, 140, 160);
	}

	.feature-item__title {
		font-size: var(--font-size-base);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-1);
	}

	.feature-item__description {
		font-size: var(--font-size-sm);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	.features__visual {
		order: 2;
		display: flex;
		justify-content: center;
	}

	.features__image {
		aspect-ratio: 16/9;
		width: 100%;
		max-width: 500px;
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.1), rgba(255, 120, 150, 0.1));
		border-radius: var(--radius-xl);
		border: 1px solid rgba(255, 94, 126, 0.2);
		box-shadow: var(--shadow-elevated);
		display: flex;
		align-items: center;
		justify-content: center;
		color: rgb(255, 94, 126);
	}

	:global(.dark) .features__image {
		background: linear-gradient(135deg, rgba(255, 94, 126, 0.15), rgba(255, 120, 150, 0.15));
		border-color: rgba(255, 94, 126, 0.3);
		color: rgb(255, 140, 160);
	}

	/* Integrations Section */
	.integrations {
		padding: var(--space-20) 0;
	}

	.integrations__header {
		text-align: center;
		margin-bottom: var(--space-12);
	}

	.integrations__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.integrations__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		max-width: 600px;
		margin: 0 auto var(--space-8);
	}

	.integrations__grid {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: var(--space-3);
	}

	.integration-tag {
		padding: var(--space-2) var(--space-4);
		background: var(--bg-primary);
		border: 1px solid var(--border-light);
		border-radius: var(--radius-lg);
		font-size: var(--font-size-sm);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
	}

	:global(.dark) .integration-tag {
		background: var(--bg-tertiary);
		border-color: var(--border-default);
	}

	/* CTA Section */
	.cta {
		padding: var(--space-20) 0;
		background: var(--bg-secondary);
		text-align: center;
		position: relative;
	}

	.cta::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.03) 0%, 
			rgba(151, 71, 255, 0.03) 100%);
		pointer-events: none;
	}

	:global(.dark) .cta {
		background: var(--bg-secondary);
	}

	:global(.dark) .cta::before {
		background: linear-gradient(135deg, 
			rgba(255, 94, 126, 0.05) 0%, 
			rgba(151, 71, 255, 0.05) 100%);
	}

	.cta__content {
		text-align: center;
		max-width: 700px;
		margin: 0 auto;
		position: relative;
	}

	.cta__title {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}

	.cta__subtitle {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.cta__actions {
		display: flex;
		gap: var(--space-4);
		justify-content: center;
		flex-wrap: wrap;
	}

	:global(.cta__button) {
		display: inline-flex;
		align-items: center;
		gap: var(--space-2);
	}

	:global(.cta__secondary) {
		border-color: var(--text-on-brand);
		color: var(--text-on-brand);
	}

	:global(.cta__secondary:hover) {
		background: var(--text-on-brand);
		color: var(--brand-primary);
	}

	:global(.dark .cta__secondary) {
		border-color: var(--border-default);
		color: var(--text-primary);
	}

	:global(.dark .cta__secondary:hover) {
		background: var(--bg-primary);
		color: var(--text-brand);
	}

	/* Responsive Design */
	@media (max-width: 968px) {
		.features__grid {
			grid-template-columns: 1fr;
			gap: var(--space-8);
		}

		.features__visual {
			order: 1;
		}

		.features__content {
			order: 2;
		}
	}

	@media (max-width: 768px) {
		.hero__title {
			font-size: 2rem;
		}

		.browsers__single {
			max-width: 100%;
		}
	}
</style>